import React, {Suspense, lazy} from 'react';
import {createBrowserRouter, RouterProvider, Navigate} from 'react-router';
import {RootRedirect} from './RootRedirect';
import {ProtectedRoute} from './ProtectedRoute';
import {LayoutWithBreadcrumbs} from '../../widgets/layout';
import {MenuItem} from '@gravity-ui/navigation';
import {Gear} from '@gravity-ui/icons';
import {
  accountMenuItems,
  fundraisingsMenuItems,
  CloudPaymentsCheckout,
  contentMenuItems,
  notificationsMenuItems,
  PageLoader as SharedPageLoader,
  withErrorBoundary,
  administrationsMenuItems,
  projectMenuItems,
  representativeMenuItems,
} from '../../shared';
import {conversationsMenuItems} from './conversationsMenu';

// Lazy loading всех страниц для code splitting
const AuthPage = lazy(() => import('../../pages/auth').then((module) => ({default: module.AuthPage})));
const AdminsPage = lazy(() => import('../../pages/admins').then((module) => ({default: module.AdminsPage})));
const AdminProfilePage = lazy(() =>
  import('../../pages/admin_profile').then((module) => ({default: module.AdminProfilePage})),
);
const FundRepresentativesPage = lazy(() =>
  import('../../pages/fund_representatives').then((module) => ({default: module.FundRepresentativesPage})),
);
const FundRepresentativeProfilePage = lazy(() =>
  import('../../pages/fund_representative_profile').then((module) => ({default: module.FundRepresentativeProfilePage})),
);
const BenefactorsPage = lazy(() =>
  import('../../pages/benefactors').then((module) => ({default: module.BenefactorsPage})),
);
const BenefactorProfilePage = lazy(() =>
  import('../../pages/benefactor_profile').then((module) => ({default: module.BenefactorProfilePage})),
);
const RegionsPage = lazy(() => import('../../pages/regions').then((module) => ({default: module.RegionsPage})));

const CategoryGroupsPage = lazy(() =>
  import('../../pages/categoryGroups').then((module) => ({default: module.CategoryGroupsPage})),
);
const CategoryGroupDetailPage = lazy(() =>
  import('../../pages/categoryGroups/detail').then((module) => ({default: module.CategoryGroupDetailPage})),
);
const FundraisersPage = lazy(() =>
  import('../../pages/fundraisers').then((module) => ({default: module.FundraisersPage})),
);
const AddAppealPage = lazy(() =>
  import('../../pages/fundraisers/create').then((module) => ({default: module.AddAppealPage})),
);
const AppealDetailPage = lazy(() =>
  import('../../pages/fundraisers/detail/AppealDetailPage').then((module) => ({
    default: module.AppealDetailPage,
  })),
);
const EditFundraiserPage = lazy(() =>
  import('../../pages/fundraisers/edit').then((module) => ({default: module.EditFundraiserPage})),
);
const AppealStoryDetailPage = lazy(() =>
  import('../../pages/fundraisers/detail/story').then((module) => ({default: module.AppealStoryDetailPage})),
);
const AppealMaterialsStoryComposerPage = lazy(() =>
  import('../../pages/fundraisers/detail/materials-story').then((module) => ({
    default: module.AppealMaterialsStoryComposerPage,
  })),
);
const CommunicationMessageDetailPage = lazy(() =>
  import('../../pages/fundraisers/detail/communication').then((module) => ({
    default: module.CommunicationMessageDetailPage,
  })),
);
const FundsPage = lazy(() => import('../../pages/funds').then((module) => ({default: module.FundsPage})));
const FundDetailPage = lazy(() =>
  import('../../pages/funds/detail').then((module) => ({default: module.FundDetailPage})),
);
const AddFundPage = lazy(() => import('../../pages/funds/create').then((module) => ({default: module.AddFundPage})));
const EditFundPage = lazy(() => import('../../pages/funds/edit').then((module) => ({default: module.EditFundPage})));
const OrderDetailPage = lazy(() =>
  import('../../pages/order_detail').then((module) => ({default: module.OrderDetailPage})),
);
const BlocksPage = lazy(() => import('../../pages/blocks').then((module) => ({default: module.BlocksPage})));
const BlockDetailPage = lazy(() =>
  import('../../pages/blocks/detail').then((module) => ({default: module.BlockDetailPage})),
);
const StoryDetailPage = lazy(() =>
  import('../../pages/blocks/story/detail').then((module) => ({default: module.StoryDetailPage})),
);
const ProjectStoriesPage = lazy(() =>
  import('../../pages/projectStories').then((module) => ({default: module.ProjectStoriesPage})),
);
const ProjectStoryDetailPage = lazy(() =>
  import('../../pages/projectStories/detail').then((module) => ({default: module.ProjectStoryDetailPage})),
);
const NotificationsPage = lazy(() =>
  import('../../pages/notifications').then((module) => ({default: module.NotificationsPage})),
);
const NotificationDetailPage = lazy(() =>
  import('../../pages/notifications/detail').then((module) => ({default: module.NotificationDetailPage})),
);
const ConsentsDocumentsPage = lazy(() =>
  import('../../pages/consents-documents').then((module) => ({default: module.ConsentsDocumentsPage})),
);
const ConsentDocumentDetailPage = lazy(() =>
  import('../../pages/consents-documents/detail').then((module) => ({default: module.ConsentDocumentDetailPage})),
);
const ClientVersionsPage = lazy(() =>
  import('../../pages/clientVersions').then((module) => ({default: module.ClientVersionsPage})),
);
const ProjectPage = lazy(() => import('../../pages/project').then((module) => ({default: module.ProjectPage})));
const PartnersPage = lazy(() => import('../../pages/partners').then((module) => ({default: module.PartnersPage})));
const PartnerDetailPage = lazy(() =>
  import('../../pages/partners/detail').then((module) => ({default: module.PartnerDetailPage})),
);
const ProjectDocumentsPage = lazy(() =>
  import('../../pages/projectDocuments').then((module) => ({default: module.ProjectDocumentsPage})),
);
const ProjectDocumentDetailPage = lazy(() =>
  import('../../pages/projectDocuments/detail').then((module) => ({default: module.ProjectDocumentDetailPage})),
);

const ApplicationsPage = lazy(() =>
  import('../../pages/applications').then((module) => ({default: module.ApplicationsPage})),
);

const ApplicationDetailPage = lazy(() =>
  import('../../pages/applications/detail').then((module) => ({default: module.ApplicationDetailPage})),
);

const ConversationsPage = lazy(() =>
  import('../../pages/conversations').then((module) => ({default: module.ConversationsPage})),
);

const LazyPageWrapper = withErrorBoundary(({children}: {children: React.ReactNode}) => (
  <Suspense fallback={<SharedPageLoader />}>{children}</Suspense>
));

// Определяем наборы menu items для разных разделов
const defaultMenuItems: MenuItem[] = [
  {id: 'dashboard', title: 'Dashboard', icon: Gear, href: '/'},
  {id: 'settings', title: 'Settings', icon: Gear, href: '/settings'},
];

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <RootRedirect />
      </ProtectedRoute>
    ),
  },
  {
    path: '/auth',
    element: (
      <LazyPageWrapper>
        <AuthPage />
      </LazyPageWrapper>
    ),
  },
  {
    path: '/cloud-payments',
    element: <CloudPaymentsCheckout />,
  },
  {
    element: (
      <ProtectedRoute>
        <LayoutWithBreadcrumbs defaultMenuItems={defaultMenuItems} />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/accounts',
        handle: {
          menuItems: accountMenuItems,
          allowedRoles: ['admin'],
        },
        children: [
          {element: <Navigate to="admins" replace />},
          {
            path: 'admins',
            element: (
              <LazyPageWrapper>
                <AdminsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'admins/profile/:id',
            element: (
              <LazyPageWrapper>
                <AdminProfilePage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'benefactors',
            element: (
              <LazyPageWrapper>
                <BenefactorsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'benefactors/profile/:id',
            element: (
              <LazyPageWrapper>
                <BenefactorProfilePage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'benefactors/profile/:benefactorId/orders/:id',
            element: (
              <LazyPageWrapper>
                <OrderDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'fund-representatives',
            element: (
              <LazyPageWrapper>
                <FundRepresentativesPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'fund-representatives/profile/:id',
            element: (
              <LazyPageWrapper>
                <FundRepresentativeProfilePage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
      {
        path: '/fundraisings',
        handle: {
          menuItems: fundraisingsMenuItems,
          allowedRoles: ['admin'],
        },
        children: [
          {element: <Navigate to="appeals" replace />},
          {
            path: 'regions',
            element: (
              <LazyPageWrapper>
                <RegionsPage />
              </LazyPageWrapper>
            ),
          },

          {
            path: 'category-groups',
            element: (
              <LazyPageWrapper>
                <CategoryGroupsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'category-groups/:id',
            element: (
              <LazyPageWrapper>
                <CategoryGroupDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'funds',
            element: (
              <LazyPageWrapper>
                <FundsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'funds/create',
            element: (
              <LazyPageWrapper>
                <AddFundPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'funds/:id',
            element: (
              <LazyPageWrapper>
                <FundDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'funds/:id/edit',
            element: (
              <LazyPageWrapper>
                <EditFundPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'appeals',
            element: (
              <LazyPageWrapper>
                <FundraisersPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'appeals/create',
            element: (
              <LazyPageWrapper>
                <AddAppealPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'appeals/:id',
            element: (
              <LazyPageWrapper>
                <AppealDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'appeals/:id/edit',
            element: (
              <LazyPageWrapper>
                <EditFundraiserPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'appeals/:appealId/stories/:storyId',
            element: (
              <LazyPageWrapper>
                <AppealStoryDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'appeals/:id/materials-story/new',
            element: (
              <LazyPageWrapper>
                <AppealMaterialsStoryComposerPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'appeals/:id/communication/:messageId',
            element: (
              <LazyPageWrapper>
                <CommunicationMessageDetailPage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
      {
        path: '/content',
        handle: {
          menuItems: contentMenuItems,
          allowedRoles: ['admin'],
        },
        children: [
          {element: <Navigate to="blocks" replace />},
          {
            path: 'blocks',
            element: (
              <LazyPageWrapper>
                <BlocksPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'blocks/:id',
            element: (
              <LazyPageWrapper>
                <BlockDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'blocks/:blockId/stories/:storyId',
            element: (
              <LazyPageWrapper>
                <StoryDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'project-stories',
            element: (
              <LazyPageWrapper>
                <ProjectStoriesPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'project-stories/:storyId',
            element: (
              <LazyPageWrapper>
                <ProjectStoryDetailPage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
      {
        path: '/notifications',
        handle: {
          menuItems: notificationsMenuItems,
          allowedRoles: ['admin'],
        },
        children: [
          {element: <Navigate to="notifications-list" replace />},
          {
            path: 'notifications-list',
            element: (
              <LazyPageWrapper>
                <NotificationsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'messages/:messageId',
            element: (
              <LazyPageWrapper>
                <NotificationDetailPage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
      {
        path: '/administration',
        handle: {
          menuItems: administrationsMenuItems,
          allowedRoles: ['admin'],
        },
        children: [
          {element: <Navigate to="consents-documents" replace />},
          {
            path: 'consents-documents',
            element: (
              <LazyPageWrapper>
                <ConsentsDocumentsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'consents-documents/:documentId',
            element: (
              <LazyPageWrapper>
                <ConsentDocumentDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'client-versions',
            element: (
              <LazyPageWrapper>
                <ClientVersionsPage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
      {
        path: '/project',
        handle: {
          menuItems: projectMenuItems,
          allowedRoles: ['admin'],
        },
        children: [
          {element: <Navigate to="" replace />},
          {
            path: '',
            element: (
              <LazyPageWrapper>
                <ProjectPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'partners',
            element: (
              <LazyPageWrapper>
                <PartnersPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'partners/:partnerId',
            element: (
              <LazyPageWrapper>
                <PartnerDetailPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'documents',
            element: (
              <LazyPageWrapper>
                <ProjectDocumentsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: 'documents/:documentId',
            element: (
              <LazyPageWrapper>
                <ProjectDocumentDetailPage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
      {
        path: '/conversations',
        handle: {
          menuItems: conversationsMenuItems,
          allowedRoles: ['admin'],
        },
        children: [
          {
            path: '',
            element: (
              <LazyPageWrapper>
                <ConversationsPage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    element: (
      <ProtectedRoute>
        <LayoutWithBreadcrumbs defaultMenuItems={defaultMenuItems} />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/applications',
        handle: {
          menuItems: representativeMenuItems,
          allowedRoles: ['representative'],
        },
        children: [
          {
            path: '',
            element: (
              <LazyPageWrapper>
                <ApplicationsPage />
              </LazyPageWrapper>
            ),
          },
          {
            path: ':appealId',
            element: (
              <LazyPageWrapper>
                <ApplicationDetailPage />
              </LazyPageWrapper>
            ),
          },
        ],
      },
    ],
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
